import React, { useContext, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AgGridEvent,
  ColDef,
  GridOptions,
  GridReadyEvent,
  RowClickedEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import styled from 'styled-components';

import CardContext from 'contexts/CardContext';
import HeadlessRefitContext from 'contexts/HeadlessRefitContext';
import ReportContext from 'contexts/ReportContext';

import StyleOverrides, {
  GRID_BORDERS_CSS,
  GRID_DARK_HEADER_CSS,
} from './StyleOverrides';
import Row from '../Common/Row';
import DetailsSlideOut from '../DetailsSlideOut';
import CustomerLaneSlideOut from '../CustomerLaneCommitsBoard/SlideOut';
import NewCommitmentSlideOut from '../CustomerLaneCommitsBoard/NewCommitPopup/NewCommitmentSlideOut';

import BoardSlideOut from '../BoardSlideOut';
import NoDataToDisplay from '../V5Gadget/NoDataToDisplay';
import FlexCentered from '../Common/FlexCentered';
import Loading from '../Loading';
import GridFrameworkComponents from './GridFrameworkComponents';
import PerformanceBoard from 'components/PerformanceBoards';
import { Z_INDEX } from '../../constants';

const Wrapper = styled.div<{ hasBorders: boolean; hasDarkHeader: boolean }>`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: row;
  position: relative;
  overflow: hidden;

  ${(props) => props.hasBorders && GRID_BORDERS_CSS}
  ${(props) => props.hasDarkHeader && GRID_DARK_HEADER_CSS}
`;

const Left = styled.div<{ isDetailsSlideOpen?: boolean }>`
  height: 100%;
  width: ${(props) => (props.isDetailsSlideOpen ? '70%' : '100%')};
`;

// Height reported for a grid with no rendered rows (loading / no data) so its
// card still shrinks in the PDF but keeps the "no data" message readable.
const MIN_MATRIX_CONTENT_PX = 120;
// Quiet window before reporting a measured height, so we measure after ag-grid's
// async column auto-size (which can wrap headers / add a horizontal scrollbar)
// has settled rather than on every intermediate resize.
const REFIT_DEBOUNCE_MS = 150;

// Under the scheduled-report PDF print route only, measure the grid's natural
// rendered height and report it up so the canvas can refit the card to content
// (removing the whitespace band below the last row). Relies on the caller having
// set domLayout:'autoHeight', which sizes .ag-root-wrapper to true content
// height independently of the (fixed) card slot — so there is no measure→resize
// feedback loop.
const useHeadlessGridRefit = ({
  doRefit,
  gridId,
  isLoading,
  noDataToDisplay,
}: {
  doRefit: boolean;
  gridId: string;
  isLoading?: boolean;
  noDataToDisplay?: boolean;
}) => {
  const { i: cardId } = useContext(CardContext);
  const { reportContentHeight } = useContext(HeadlessRefitContext);

  useEffect(() => {
    if (!doRefit || !cardId) {
      return;
    }

    // Loading / empty branches render a message instead of the grid, so there is
    // nothing to measure — report a small height and hold it.
    if (isLoading || noDataToDisplay) {
      reportContentHeight(cardId, MIN_MATRIX_CONTENT_PX);
      return () => reportContentHeight(cardId, undefined);
    }

    let cancelled = false;
    let observer: ResizeObserver | undefined;
    let poll: ReturnType<typeof setInterval> | undefined;
    let debounce: ReturnType<typeof setTimeout> | undefined;

    const report = (root: HTMLElement) => {
      if (debounce) {
        clearTimeout(debounce);
      }
      debounce = setTimeout(() => {
        if (!cancelled) {
          reportContentHeight(cardId, root.offsetHeight);
        }
      }, REFIT_DEBOUNCE_MS);
    };

    const attach = () => {
      const root = document
        .getElementById(gridId)
        ?.querySelector('.ag-root-wrapper') as HTMLElement | null;
      if (!root) {
        return false;
      }
      observer = new ResizeObserver(() => report(root));
      observer.observe(root);
      report(root);
      return true;
    };

    // The grid renders asynchronously, so poll until .ag-root-wrapper exists.
    if (!attach()) {
      poll = setInterval(() => {
        if (cancelled) {
          return;
        }
        if (attach() && poll) {
          clearInterval(poll);
        }
      }, 100);
    }

    return () => {
      cancelled = true;
      if (poll) {
        clearInterval(poll);
      }
      if (debounce) {
        clearTimeout(debounce);
      }
      observer?.disconnect();
      reportContentHeight(cardId, undefined);
    };
  }, [
    doRefit,
    gridId,
    cardId,
    isLoading,
    noDataToDisplay,
    reportContentHeight,
  ]);
};

// To be used in search screen, popup grids and boards
const buildOnFirstDataRendered = ({
  gridId,
  disableAutoLayout,
  skipFirstLayout,
  autoSizeFirstColumnOnly,
  autoSizeSecondColumnOnly,
  additionalAutoSizeColumnIds,
  skipHeader,
}: {
  gridId: string;
  disableAutoLayout?: boolean;
  skipFirstLayout?: boolean;
  autoSizeFirstColumnOnly?: boolean;
  autoSizeSecondColumnOnly?: boolean;
  // Extra column ids to auto-size alongside the first column in
  // `autoSizeFirstColumnOnly` mode (e.g. the reversed-matrix pinned Total
  // column, which otherwise keeps ag-grid's default width because pinned
  // columns ignore `flex`). Unknown ids are a safe no-op.
  additionalAutoSizeColumnIds?: string[];
  skipHeader?: boolean;
}) => {
  let skipLayout = !!skipFirstLayout;

  return (event: AgGridEvent) => {
    if (disableAutoLayout || skipLayout) {
      skipLayout = false;
      return;
    }

    const interval = setInterval(() => {
      const isReady = (() => {
        if (document.visibilityState !== 'visible') {
          return false;
        }
        const elem = document.getElementById(gridId);
        if (elem) {
          const container = elem.getElementsByClassName(
            'ag-center-cols-container',
          )[0];
          if (container) {
            const rows = container.getElementsByClassName('ag-row').length;
            if (rows > 0) {
              return true;
            }
          }
        }
        return false;
      })();

      if (isReady) {
        clearInterval(interval);

        const cols = event.api.getAllDisplayedColumns();
        if (cols === undefined || cols.length === 0) {
          return;
        }

        if (process.env.NODE_ENV === 'test') {
          return;
        }

        if (autoSizeFirstColumnOnly) {
          event.api.autoSizeColumns([
            cols[0].getId(),
            ...(additionalAutoSizeColumnIds ?? []),
          ]);
        } else if (autoSizeSecondColumnOnly) {
          event.api.autoSizeColumns([cols[1].getId()]);
        } else {
          event.api.autoSizeAllColumns(skipHeader !== false);
        }
      }
    });
  };
};

const Grid = ({
  rowData,
  columnDefs,
  columnDefs2,
  onGridReady,
  gridOptions,
  totalsRow,
  gridId,
  bottomBar,
  isDetailsSlideOpen,
  isCustomerLaneSlideOpen,
  isNewCommitmentSlideOpen,
  isBoard,
  onRowClicked,
  onSortChanged,
  isPerformanceSettings,
  noDataToDisplay,
  isLoading,
  disableAutoLayout,
  layoutOnColumnChange,
  layoutOnFirstRender,
  layoutOnModelUpdated,
  autoSizeFirstColumnOnly,
  autoSizeSecondColumnOnly,
  additionalAutoSizeColumnIds,
  autoSizeSkipHeader,
  hasBorders,
  hasDarkHeader,
  isSearchScreen,
  enableHeadlessRefit,
}: {
  rowData?: any[];
  columnDefs: ColDef[];
  columnDefs2?: MatrixColDef[];
  gridOptions: GridOptions;
  gridId: string;
  totalsRow?: any;
  onGridReady?: (event: GridReadyEvent) => void;
  bottomBar?: JSX.Element | JSX.Element[];
  isDetailsSlideOpen?: boolean;
  isCustomerLaneSlideOpen?: boolean;
  isNewCommitmentSlideOpen?: boolean;
  isBoard?: boolean;
  onRowClicked?: (event: RowClickedEvent) => void;
  onSortChanged?: (event: SortChangedEvent) => void;
  disableAutoLayout?: boolean;
  isPerformanceSettings?: boolean;
  noDataToDisplay?: boolean;
  isLoading?: boolean;
  layoutOnModelUpdated?: boolean;
  layoutOnFirstRender?: boolean;
  layoutOnColumnChange?: boolean;
  autoSizeFirstColumnOnly?: boolean;
  autoSizeSecondColumnOnly?: boolean;
  additionalAutoSizeColumnIds?: string[];
  autoSizeSkipHeader?: boolean;
  hasBorders: boolean;
  hasDarkHeader: boolean;
  isSearchScreen?: boolean;
  // Scheduled-report PDF only: opt this grid into content-fit refitting (see
  // useHeadlessGridRefit). Only the ag-grid matrix gadgets set this.
  enableHeadlessRefit?: boolean;
}) => {
  const { isPdfExport } = useContext(ReportContext);
  const doRefit = !!enableHeadlessRefit && !!isPdfExport;

  useHeadlessGridRefit({ doRefit, gridId, isLoading, noDataToDisplay });

  // In the PDF path, let ag-grid size itself to its rows so the measured height
  // reflects real content and there is no internal scroll after the card refits.
  const effectiveGridOptions: GridOptions = doRefit
    ? { ...gridOptions, domLayout: 'autoHeight' }
    : gridOptions;

  return (
    <>
      <Wrapper
        id={gridId}
        className={'gridWrapper'}
        data-testid="grid"
        hasBorders={hasBorders}
        hasDarkHeader={hasDarkHeader}
      >
        <Left isDetailsSlideOpen={isDetailsSlideOpen}>
          {isLoading && (
            <FlexCentered style={{ height: '100%' }}>
              <Loading />
            </FlexCentered>
          )}
          {noDataToDisplay && !isLoading && (
            <FlexCentered style={{ height: '100%' }}>
              <NoDataToDisplay size={'normal'} />
            </FlexCentered>
          )}
          {!noDataToDisplay && !isLoading && (
            <StyleOverrides
              isPerformanceSettings={isPerformanceSettings}
              clickable={!!onRowClicked}
              style={{
                height: '100%',
                width: '100%',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: '100%',
                }}
                className={`ag-theme-balham ag-theme-fleetops ${isSearchScreen ? 'ag-theme-fleetops-search' : ''}`}
              >
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs2 ? columnDefs2 : columnDefs}
                  onRowClicked={onRowClicked}
                  onGridReady={onGridReady}
                  gridOptions={effectiveGridOptions}
                  pinnedBottomRowData={totalsRow ? totalsRow : undefined}
                  components={GridFrameworkComponents}
                  loadingOverlayComponent={Loading}
                  onSortChanged={onSortChanged}
                  onFirstDataRendered={
                    layoutOnFirstRender
                      ? buildOnFirstDataRendered({
                          gridId,
                          disableAutoLayout,
                          skipFirstLayout: false,
                          autoSizeFirstColumnOnly,
                          autoSizeSecondColumnOnly,
                          additionalAutoSizeColumnIds,
                          skipHeader: autoSizeSkipHeader,
                        })
                      : undefined
                  }
                  onModelUpdated={
                    layoutOnModelUpdated
                      ? buildOnFirstDataRendered({
                          gridId,
                          disableAutoLayout,
                          skipFirstLayout: true,
                          autoSizeFirstColumnOnly,
                          autoSizeSecondColumnOnly,
                          additionalAutoSizeColumnIds,
                          skipHeader: autoSizeSkipHeader,
                        })
                      : undefined
                  }
                  onGridColumnsChanged={
                    layoutOnColumnChange
                      ? buildOnFirstDataRendered({
                          gridId,
                          disableAutoLayout,
                          skipFirstLayout: true,
                          autoSizeFirstColumnOnly,
                          autoSizeSecondColumnOnly,
                          additionalAutoSizeColumnIds,
                          skipHeader: autoSizeSkipHeader,
                        })
                      : undefined
                  }
                />
              </div>
            </StyleOverrides>
          )}
          <Row style={{ paddingLeft: 11, paddingRight: 11 }}>{bottomBar}</Row>
        </Left>
        {isDetailsSlideOpen && (
          <div
            style={{
              height: '100%',
              minWidth: 275,
              width: '30%',
              zIndex: Z_INDEX.GRID_SLIDE_OUT,
              fontSize: 11,
            }}
          >
            <DetailsSlideOut />
          </div>
        )}
        {isCustomerLaneSlideOpen && (
          <div
            style={{
              height: '100%',
              minWidth: 500,
              width: '35%',
              zIndex: Z_INDEX.GRID_SLIDE_OUT,
              fontSize: 11,
            }}
          >
            <CustomerLaneSlideOut />
          </div>
        )}
        {!isBoard && isNewCommitmentSlideOpen && (
          <div
            style={{
              height: '100%',
              minWidth: 275,
              width: '35%',
              zIndex: Z_INDEX.GRID_SLIDE_OUT,
              fontSize: 11,
            }}
          >
            <NewCommitmentSlideOut />
          </div>
        )}
        <BoardSlideOut />
        <PerformanceBoard.SlideOut />
      </Wrapper>
    </>
  );
};

export default Grid;
